import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { getDataAPI } from '../../utils/fetchData';

const MetricCard = ({ title, value, subtitle, color = '#FF6B35' }) => (
    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[rgba(255,255,255,0.2)] transition-all">
        <div className="flex items-center gap-2 mb-2">
            <div 
                className="w-[8px] h-[8px] rounded-full"
                style={{ backgroundColor: color }}
            />
            <span className="text-[10px] text-[rgba(255,255,255,0.7)] uppercase tracking-wider">
                {title}
            </span>
        </div>
        <div className="text-white text-[18px] font-bold mb-1">{value}</div>
        {subtitle && (
            <div className="text-[rgba(255,255,255,0.5)] text-[10px]">{subtitle}</div>
        )}
    </div>
);

const ContentCard = ({ title, children, color = '#FF6B35' }) => (
    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[rgba(255,255,255,0.2)] transition-all">
        <div className="flex items-center gap-2 mb-4">
            <div 
                className="w-[8px] h-[8px] rounded-full"
                style={{ backgroundColor: color }}
            />
            <span className="text-[10px] text-[rgba(255,255,255,0.7)] uppercase tracking-wider">
                {title}
            </span>
        </div>
        {children}
    </div>
);

const TourManagerAnalytics = () => {
    const navigate = useNavigate();
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const token = localStorage.getItem('dizeeToken');

    useEffect(() => {
        loadAnalytics();
    }, [filter]);

    const loadAnalytics = async () => {
        try {
            setLoading(true);
            const response = await getDataAPI(`user/tm/analytics?filter=${filter}`, token);
            console.log('Tour Analytics Response:', response);
            
            // Match the structure used in BookingsAnalytics and CatalogAnalytics
            if (response.data?.data) {
                setAnalyticsData(response.data.data);
            } else if (response.data) {
                setAnalyticsData(response.data);
            } else {
                setAnalyticsData(null);
            }
        } catch (error) {
            console.error('Failed to load analytics:', error);
            setAnalyticsData(null);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        if (!amount || isNaN(amount)) return '$0';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const formatNumber = (num) => {
        if (!num || isNaN(num)) return '0';
        return new Intl.NumberFormat('en-US').format(num);
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <LayoutHeader>
                <div className="flex justify-center w-full">
                    <div className="w-[390px] h-screen bg-black text-white flex items-center justify-center">
                        <p className="text-[12px]">Loading analytics...</p>
                    </div>
                </div>
            </LayoutHeader>
        );
    }

    return (
        <LayoutHeader>
            <div className="flex justify-center w-full">
                <div className="w-[390px] h-screen bg-black text-white relative">
                    <div className="flex flex-col h-full">
                        {/* Header */}
                        <div className="px-4 my-[50px] flex w-full justify-between items-center">
                            <div className="flex items-center gap-2">
                                <div 
                                    className="w-[24px] h-[24px] rounded-md flex items-center justify-center"
                                    style={{ backgroundColor: '#FF6B35' }}
                                />
                                <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Tour Manager Analytics</span>
                            </div>
                            <div className="flex items-center gap-4">
                                <p className='text-[#FF6B35] cursor-pointer text-[12px] hover:opacity-70 transition-opacity'>Export data</p>
                                <div 
                                    className="text-white cursor-pointer text-[12px] hover:text-gray-400 transition-colors"
                                    onClick={() => navigate('/analytics/hub')}
                                >
                                    Back
                                </div>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="w-full px-4 flex-1 overflow-y-auto pb-[100px]">
                            {/* Filter Dropdown */}
                            <div className="mb-6">
                                <select 
                                    value={filter}
                                    onChange={(e) => setFilter(e.target.value)}
                                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white text-[12px] px-4 py-3 rounded-lg focus:outline-none focus:border-[#FF6B35] transition-colors appearance-none cursor-pointer"
                                    style={{
                                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='white' stroke-opacity='0.5' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                                        backgroundRepeat: 'no-repeat',
                                        backgroundPosition: 'right 1rem center'
                                    }}
                                >
                                    <option value="all" className="bg-black">All Time</option>
                                    <option value="upcoming" className="bg-black">Upcoming Shows</option>
                                    <option value="past" className="bg-black">Past Shows</option>
                                    <option value="current" className="bg-black">Current Tours</option>
                                    <option value="month" className="bg-black">This Month</option>
                                    <option value="year" className="bg-black">This Year</option>
                                </select>
                            </div>

                            {analyticsData && analyticsData.totalShows > 0 ? (
                                <>
                                    {/* Overview Metrics */}
                                    <div className="grid grid-cols-2 gap-4 mb-6">
                                        <MetricCard 
                                            title="Total Shows" 
                                            value={formatNumber(analyticsData.totalShows)}
                                            subtitle={`${formatNumber(analyticsData.upcomingShows || 0)} upcoming`}
                                        />
                                        <MetricCard 
                                            title="Active Tours" 
                                            value={formatNumber(analyticsData.activeTours || 0)}
                                            subtitle={`${formatNumber(analyticsData.completedTours || 0)} completed`}
                                        />
                                        <MetricCard 
                                            title="Total Budget" 
                                            value={formatCurrency(analyticsData.totalBudget)}
                                            subtitle={`${formatCurrency(analyticsData.totalSpent || 0)} spent`}
                                        />
                                        <MetricCard 
                                            title="Team Members" 
                                            value={formatNumber(analyticsData.totalCrew || 0)}
                                            subtitle={`${formatNumber(analyticsData.uniqueVenues || 0)} venues`}
                                        />
                                    </div>

                                    {/* Travel Statistics */}
                                    {(analyticsData.totalFlights > 0 || analyticsData.totalHotels > 0 || analyticsData.totalGroundTransport > 0) && (
                                        <div className="mb-6">
                                            <ContentCard title="Travel & Logistics">
                                                <div className="space-y-3">
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-[10px] text-gray-400">Flights Booked</span>
                                                        <span className="text-[10px] text-white">{formatNumber(analyticsData.totalFlights || 0)}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-[10px] text-gray-400">Hotel Nights</span>
                                                        <span className="text-[10px] text-white">{formatNumber(analyticsData.totalHotels || 0)}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-[10px] text-gray-400">Ground Transport</span>
                                                        <span className="text-[10px] text-white">{formatNumber(analyticsData.totalGroundTransport || 0)}</span>
                                                    </div>
                                                    {analyticsData.travelBudget && (
                                                        <div className="flex justify-between items-center pt-2 border-t border-[rgba(255,255,255,0.1)]">
                                                            <span className="text-[10px] text-gray-400">Travel Budget</span>
                                                            <span className="text-[10px] text-white font-semibold">{formatCurrency(analyticsData.travelBudget)}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </ContentCard>
                                        </div>
                                    )}

                                    {/* Show Status Breakdown */}
                                    {analyticsData.statusBreakdown && Object.keys(analyticsData.statusBreakdown).length > 0 && (
                                        <div className="mb-6">
                                            <ContentCard title="Show Status">
                                                <div className="space-y-3">
                                                    {Object.entries(analyticsData.statusBreakdown).map(([status, count]) => (
                                                        <div key={status} className="flex justify-between items-center">
                                                            <span className="text-[10px] text-white capitalize">{status.replace('_', ' ')}</span>
                                                            <span className="text-[10px] text-gray-400">{count} shows</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </ContentCard>
                                        </div>
                                    )}

                                    {/* Top Cities */}
                                    {analyticsData.topCities && analyticsData.topCities.length > 0 && (
                                        <div className="mb-6">
                                            <ContentCard title="Top Cities">
                                                <div className="space-y-3">
                                                    {analyticsData.topCities.map((city, index) => (
                                                        <div key={index} className="flex justify-between items-center">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-[10px] text-[rgba(255,255,255,0.4)] w-[15px]">#{index + 1}</span>
                                                                <span className="text-[10px] text-white">{city.city}</span>
                                                            </div>
                                                            <span className="text-[10px] text-gray-400">{city.shows} shows</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </ContentCard>
                                        </div>
                                    )}

                                    {/* Upcoming Shows Timeline */}
                                    {analyticsData.upcomingShowsList && analyticsData.upcomingShowsList.length > 0 && (
                                        <div className="mb-6">
                                            <ContentCard title="Next Shows">
                                                <div className="space-y-3">
                                                    {analyticsData.upcomingShowsList.slice(0, 5).map((show, index) => (
                                                        <div 
                                                            key={index} 
                                                            className="flex justify-between items-start cursor-pointer hover:bg-[rgba(255,255,255,0.05)] p-2 -m-2 rounded transition-all"
                                                            onClick={() => navigate(`/tour-manager/show/${show.id}`)}
                                                        >
                                                            <div>
                                                                <span className="text-[10px] text-white block mb-1">{show.venueName}</span>
                                                                <span className="text-[8px] text-gray-400">{show.city}</span>
                                                            </div>
                                                            <span className="text-[8px] text-[#FF6B35]">{formatDate(show.date)}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </ContentCard>
                                        </div>
                                    )}

                                    {/* Budget Breakdown */}
                                    {analyticsData.budgetBreakdown && (
                                        <div className="mb-6">
                                            <ContentCard title="Budget Breakdown">
                                                <div className="space-y-3">
                                                    {analyticsData.budgetBreakdown.travel && (
                                                        <div className="flex justify-between items-center">
                                                            <span className="text-[10px] text-gray-400">Travel</span>
                                                            <span className="text-[10px] text-white">{formatCurrency(analyticsData.budgetBreakdown.travel)}</span>
                                                        </div>
                                                    )}
                                                    {analyticsData.budgetBreakdown.accommodation && (
                                                        <div className="flex justify-between items-center">
                                                            <span className="text-[10px] text-gray-400">Accommodation</span>
                                                            <span className="text-[10px] text-white">{formatCurrency(analyticsData.budgetBreakdown.accommodation)}</span>
                                                        </div>
                                                    )}
                                                    {analyticsData.budgetBreakdown.production && (
                                                        <div className="flex justify-between items-center">
                                                            <span className="text-[10px] text-gray-400">Production</span>
                                                            <span className="text-[10px] text-white">{formatCurrency(analyticsData.budgetBreakdown.production)}</span>
                                                        </div>
                                                    )}
                                                    {analyticsData.budgetBreakdown.other && (
                                                        <div className="flex justify-between items-center">
                                                            <span className="text-[10px] text-gray-400">Other</span>
                                                            <span className="text-[10px] text-white">{formatCurrency(analyticsData.budgetBreakdown.other)}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </ContentCard>
                                        </div>
                                    )}

                                    {/* Crew Statistics */}
                                    {analyticsData.crewStats && (
                                        <div className="mb-6">
                                            <ContentCard title="Crew & Personnel">
                                                <div className="space-y-3">
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-[10px] text-gray-400">Tour Manager</span>
                                                        <span className="text-[10px] text-white">{analyticsData.crewStats.tourManager || 0}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-[10px] text-gray-400">Production Crew</span>
                                                        <span className="text-[10px] text-white">{analyticsData.crewStats.production || 0}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-[10px] text-gray-400">Support Staff</span>
                                                        <span className="text-[10px] text-white">{analyticsData.crewStats.support || 0}</span>
                                                    </div>
                                                </div>
                                            </ContentCard>
                                        </div>
                                    )}

                                    {/* Tour History Chart Placeholder */}
                                    <div className="mb-6">
                                        <div className="bg-[#1A1A1A] rounded-lg p-4 border border-[#333]">
                                            <h3 className="text-white text-[12px] mb-3 flex items-center gap-2">
                                                <div className="w-[8px] h-[8px] rounded-full bg-[#FF6B35]" />
                                                Show Timeline
                                            </h3>
                                            <div className="h-[120px] bg-[#0A0A0A] rounded flex items-center justify-center">
                                                <p className="text-gray-500 text-[10px]">TIMELINE VISUALIZATION</p>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="py-[40px]">
                                    <h3 className="text-white text-[12px] font-normal mb-2">No tour data available</h3>
                                    <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6">
                                        Start adding your tour shows to see comprehensive analytics.
                                    </p>
                                    <button 
                                        onClick={() => navigate('/tour-manager')}
                                        className="text-black px-5 py-2 rounded-lg text-[12px] transition-all hover:opacity-90"
                                        style={{ backgroundColor: '#FF6B35' }}
                                    >
                                        Add Tour Shows
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Help Overlay */}
                        <div className="absolute bottom-0 left-0 right-0 h-[100px] bg-black flex items-end justify-center pb-4">
                            <p className="text-gray-400 text-[10px] text-center px-4 leading-relaxed">
                                Track your tour performance, logistics, budgets, and travel statistics.<br/>
                                View insights across all your tours and shows.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </LayoutHeader>
    );
};

export default TourManagerAnalytics;

